import React, { useState } from 'react';
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { updateEmployeeBalance } from "../services/api_balances";
import Swal from 'sweetalert2';
import { Link } from 'wouter';



const ResiduiContent = ({ employee, employeeBalances, token, onBalanceUpdated }) => {
  const [editableBalanceId, setEditableBalanceId] = useState(null);
  const [newBalanceValue, setNewBalanceValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const formatUpdateDate = (dateString) => {
    if (typeof dateString !== 'string') {
      return '';
    }

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatMinutesToHours = (minutes) => {
    const sign = minutes < 0 ? '-' : '';
    const absMinutes = Math.abs(minutes);
    const totalHours = Math.floor(absMinutes / 60);
    const remainingMinutes = absMinutes % 60;

    if (totalHours === 0 && remainingMinutes === 0) {
      return '0 min';
    } else if (totalHours === 0) {
      return `${sign} ${remainingMinutes} min`;
    } else if (remainingMinutes === 0) {
      return `${sign} ${totalHours} h`;
    } else {
      return `${sign} ${totalHours} h e ${remainingMinutes} min`;
    }
  };

  const handleEditClick = (balanceId, currentValue) => {
    setEditableBalanceId(balanceId);
    setNewBalanceValue((currentValue / 60).toFixed(2));
    setEditing(true);
  };

  const handleCancelClick = () => {
    setEditableBalanceId(null);
    setNewBalanceValue('');
    setEditing(false);
  };

  const handleSaveClick = async (balanceId) => {
    const numericValue = parseFloat(newBalanceValue.replace(',', '.')); 

    if (!isNaN(numericValue) && editableBalanceId !== null) {
      try {
        setIsSaving(true);

        const minutesValue = Math.round(numericValue * 60);
        
        await updateEmployeeBalance(token, employee.id, balanceId, minutesValue);
        const updatedBalance = employeeBalances.find(b => b.id === balanceId);
        if (updatedBalance) {
          updatedBalance.value = minutesValue;
          onBalanceUpdated(updatedBalance);
        }
        setEditableBalanceId(null);
        setNewBalanceValue('');
        setEditing(false);
        setIsSaving(false);
        Swal.fire({
          icon: 'success',
          title: 'Salvato!',
          showConfirmButton: false,
          timer: 1500
        });
      } catch (error) {
        setIsSaving(false);
        console.error("Errore nell'aggiornamento del residuo:", error);
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: "Si è verificato un errore durante il salvataggio!",
        });
      }
    }
  };

  const handleChangeNewBalanceValue = (e) => {
    let inputValue = e.target.value;
  
    inputValue = inputValue.replace(/,/g, '.');
  
    if (inputValue.startsWith('-')) {
      inputValue = '-' + inputValue.slice(1).replace(/[^0-9.]/g, '');
    } else {
      inputValue = inputValue.replace(/[^0-9.-]/g, '');
    }
  
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      inputValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
  
    setNewBalanceValue(inputValue);
  };

  return (
    <div>
      {employeeBalances && employeeBalances.length > 0 ? (
        <div className="min-w-full">
          {employeeBalances.map((balance) => (
            <div key={balance.id} className="mb-3 border-b">
              <div className="uppercase font-semibold text-indigo-700">
                {balance.rateo.name}
              </div>
              <div className="grid grid-cols-2">
                <div className="col col-span-1 text-left text-sm py-1 font-semibold text-gray-700">
                  Valore
                </div>
                <div className="col col-span-1 text-left text-sm py-1 text-gray-700">
                  {editableBalanceId === balance.id ? (
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={newBalanceValue}
                        onChange={handleChangeNewBalanceValue}
                        className="border border-gray-300 rounded-sm p-1 w-44"
                      />
                      <span className="text-xs ml-2">Inserire valore in ORE (0.50 corrisponde a 30 minuti)</span>
                    </div>
                  ) : (
                    formatMinutesToHours(balance.value)
                  )}
                  {editableBalanceId === balance.id && (
                    <>
                      <button
                        onClick={handleCancelClick}
                        className="me-2 w-20 justify-center mt-2 inline-flex border shadow-sm items-center rounded-sm bg-transparent px-2 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={() => handleSaveClick(balance.id)}
                        className="ml-2 w-20 justify-center mt-2 inline-flex items-center rounded-sm bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        disabled={isSaving} 
                      >
                        {isSaving ? 'Salvataggio...' : 'Salva'}
                      </button>
                    </>
                  )}
                  {!editing && (
                    <PencilSquareIcon
                      className="h-5 w-5 ms-3 items-center mb-1 inline-block cursor-pointer"
                      title="Modifica Residuo"
                      onClick={() => handleEditClick(balance.id, balance.value)}
                    />
                  )}
                </div>
                <div className="col col-span-1 text-left text-sm py-1 font-semibold text-gray-700">
                  Ultimo Aggiornamento
                </div>
                <div className="col col-span-1 text-left text-sm py-1 text-gray-700 pb-3">
                  {formatUpdateDate(balance.updated_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="m-8 border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm font-semibold text-gray-900">
          <div className="text-center p-5">
            <p>Non sono presenti residui. <br/> Crea un nuovo rateo per gestire il relativo residuo.</p>
            <div className="pt-2 pb-4">
              <Link
                to={`/dipendenti/${employee.cf}/edit?tab=3`}
                className="relative p-2 justify-center mt-2 inline-flex items-center rounded-sm bg-indigo-600 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Vai alla creazione dei ratei
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResiduiContent;
